/**
 * Fila de envio — vive no app, não na tela.
 *
 * A pessoa escolhe as fotos e pode sair da tela: o envio continua enquanto o
 * app estiver aberto (o que uma PWA consegue). Cada foto passa por: comprimir
 * → pedir URLs → enviar as três variantes → confirmar. Falha em qualquer
 * passo deixa a foto em `erro`, com o arquivo ainda na memória, para "Tentar
 * de novo" — e o id (UUIDv7) é o mesmo, então o servidor não duplica.
 *
 * A fila é DURÁVEL (rodada 3, B4): cada foto vai para o IndexedDB no momento
 * em que a pessoa a escolhe, e só sai quando sobe ou é descartada. Fechar o
 * app no meio — comum no parque, sem sinal — não perde nada: ao abrir, a
 * fila é restaurada e retomada; ao voltar a rede, também. O que estava
 * "enviando" quando o app fechou volta como "aguardando" (o id é o mesmo, o
 * servidor não duplica).
 */
import { uuidv7 } from '@nv/core';
import type { FotoPendente } from '@nv/offline';
import { create } from 'zustand';

import { localDb } from '../roteiro/persistence';
import { enviarVariante, MediaApiError, memoriasApi } from './api';
import { variantes } from './imagem';

export type EstadoDoItem = 'aguardando' | 'comprimindo' | 'enviando' | 'concluida' | 'erro';

export interface ItemDaFila {
  id: string;
  arquivo: File;
  nome: string;
  tripId: string;
  dayId: string;
  activityId: string | null;
  estado: EstadoDoItem;
  erro: string | null;
  /** Prévia local para a tela mostrar o que está subindo. */
  previaUrl: string;
}

interface Fila {
  itens: ItemDaFila[];
  emAndamento: boolean;
  /** Já leu o IndexedDB nesta abertura do app. */
  restaurada: boolean;
  adicionar: (arquivos: File[], destino: { tripId: string; dayId: string; activityId: string | null }) => void;
  tentarDeNovo: (id: string) => void;
  descartar: (id: string) => void;
  limparConcluidas: () => void;
  /** Traz do IndexedDB o que ficou de outra abertura e retoma o envio. */
  restaurar: () => Promise<void>;
  /** Retoma o que está aguardando (rede de volta). */
  retomar: () => void;
  /** Sair da conta: nada de outra pessoa fica no aparelho. */
  esvaziar: () => Promise<void>;
}

const PARALELO = 2;

/** O que vai para o disco: sem a prévia (é uma URL de objeto, morre com a aba). */
function paraDisco(i: ItemDaFila, estado: 'aguardando' | 'erro', erro: string | null, createdAt: number): FotoPendente {
  return { id: i.id, tripId: i.tripId, dayId: i.dayId, activityId: i.activityId, nome: i.nome, arquivo: i.arquivo, lastModified: i.arquivo.lastModified, estado, erro, createdAt };
}
async function guardar(item: FotoPendente): Promise<void> {
  try {
    await (await localDb()).fotos.put(item);
  } catch {
    // Sem IndexedDB (modo privado, cota): a fila segue em memória, como antes.
  }
}
async function apagarDoDisco(id: string): Promise<void> {
  try {
    await (await localDb()).fotos.remove(id);
  } catch {
    /* idem */
  }
}

export const useFila = create<Fila>((set, get) => {
  const marcar = (id: string, patch: Partial<ItemDaFila>) =>
    set((s) => ({ itens: s.itens.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));

  async function processar(item: ItemDaFila): Promise<void> {
    try {
      marcar(item.id, { estado: 'comprimindo', erro: null });
      const v = await variantes(item.arquivo);
      marcar(item.id, { estado: 'enviando' });
      const pedido = await memoriasApi.criarEnvio(item.tripId, {
        id: item.id,
        itineraryDayId: item.dayId,
        activityId: item.activityId,
        mimeType: v.tipo,
        bytes: v.otimizada.size,
        width: v.width,
        height: v.height,
        takenAt: item.arquivo.lastModified ? new Date(item.arquivo.lastModified).toISOString() : null,
      });
      await Promise.all([
        enviarVariante(pedido.uploads.optimized, v.otimizada),
        enviarVariante(pedido.uploads.preview, v.preview),
        enviarVariante(pedido.uploads.thumb, v.thumb),
      ]);
      await memoriasApi.concluirEnvio(item.tripId, item.id);
      marcar(item.id, { estado: 'concluida' });
      await apagarDoDisco(item.id);
      window.dispatchEvent(new CustomEvent('nv:foto-enviada', { detail: { tripId: item.tripId, dayId: item.dayId } }));
    } catch (e) {
      // Sem rede não é erro da foto: ela volta para a fila e sobe quando a rede voltar.
      if (!navigator.onLine) {
        marcar(item.id, { estado: 'aguardando', erro: null });
        return;
      }
      const mensagem =
        e instanceof MediaApiError
          ? e.message
          : e instanceof Error && /decod|codificar|createImageBitmap/i.test(e.message)
            ? 'Este arquivo não pôde ser lido como imagem.'
            : e instanceof Error
              ? e.message
              : 'Falha ao enviar.';
      marcar(item.id, { estado: 'erro', erro: mensagem });
      const atual = get().itens.find((i) => i.id === item.id);
      if (atual) await guardar(paraDisco(atual, 'erro', mensagem, atual.arquivo.lastModified || Date.now()));
    }
  }

  async function bombear(): Promise<void> {
    if (get().emAndamento) return;
    set({ emAndamento: true });
    try {
      for (;;) {
        // Sem rede, nem tenta: o `online` retoma.
        if (!navigator.onLine) break;
        const proximos = get().itens.filter((i) => i.estado === 'aguardando').slice(0, PARALELO);
        if (proximos.length === 0) break;
        await Promise.all(proximos.map(processar));
      }
    } finally {
      set({ emAndamento: false });
    }
  }

  return {
    itens: [],
    emAndamento: false,
    restaurada: false,

    adicionar: (arquivos, destino) => {
      const agora = Date.now();
      const novos: ItemDaFila[] = arquivos
        .filter((a) => a.type.startsWith('image/') || a.type === '')
        .map((arquivo) => ({
          id: uuidv7(),
          arquivo,
          nome: arquivo.name,
          ...destino,
          estado: 'aguardando',
          erro: null,
          previaUrl: URL.createObjectURL(arquivo),
        }));
      set((s) => ({ itens: [...s.itens, ...novos] }));
      // Primeiro o disco, depois a rede: se o app fechar agora, a foto já está guardada.
      void Promise.all(novos.map((n, i) => guardar(paraDisco(n, 'aguardando', null, agora + i)))).then(() => bombear());
    },

    tentarDeNovo: (id) => {
      marcar(id, { estado: 'aguardando', erro: null });
      const item = get().itens.find((i) => i.id === id);
      if (item) void guardar(paraDisco(item, 'aguardando', null, Date.now()));
      void bombear();
    },

    descartar: (id) => {
      const item = get().itens.find((i) => i.id === id);
      if (item) URL.revokeObjectURL(item.previaUrl);
      set((s) => ({ itens: s.itens.filter((i) => i.id !== id) }));
      void apagarDoDisco(id);
    },

    restaurar: async () => {
      if (get().restaurada) return;
      set({ restaurada: true });
      let pendentes: FotoPendente[] = [];
      try {
        pendentes = await (await localDb()).fotos.list();
      } catch {
        return;
      }
      const conhecidos = new Set(get().itens.map((i) => i.id));
      const restauradas: ItemDaFila[] = pendentes
        .filter((f) => !conhecidos.has(f.id))
        .map((f) => {
          const arquivo = new File([f.arquivo], f.nome, { type: f.arquivo.type || 'image/jpeg', lastModified: f.lastModified });
          return { id: f.id, arquivo, nome: f.nome, tripId: f.tripId, dayId: f.dayId, activityId: f.activityId, estado: f.estado, erro: f.erro, previaUrl: URL.createObjectURL(arquivo) };
        });
      if (restauradas.length === 0) return;
      set((s) => ({ itens: [...restauradas, ...s.itens] }));
      if (navigator.onLine) void bombear();
    },

    limparConcluidas: () => {
      for (const i of get().itens) if (i.estado === 'concluida') URL.revokeObjectURL(i.previaUrl);
      set((s) => ({ itens: s.itens.filter((i) => i.estado !== 'concluida') }));
    },

    retomar: () => {
      void bombear();
    },

    esvaziar: async () => {
      for (const i of get().itens) URL.revokeObjectURL(i.previaUrl);
      set({ itens: [], restaurada: false });
      try {
        await (await localDb()).fotos.clear();
      } catch {
        /* sem IndexedDB */
      }
    },
  };
});

// Rede de volta: o que falhou por falta dela tenta de novo, e o que está
// aguardando sobe sozinho — sem a pessoa abrir a tela.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    const s = useFila.getState();
    for (const i of s.itens) if (i.estado === 'erro') s.tentarDeNovo(i.id);
    s.retomar();
  });
}

/** Resumo para a tela: "Enviando… 2 de 3", "3 fotos enviadas", "1 de 3 não subiu". */
export function resumoDaFila(itens: readonly Pick<ItemDaFila, 'estado'>[]) {
  const total = itens.length;
  const concluidas = itens.filter((i) => i.estado === 'concluida').length;
  const erros = itens.filter((i) => i.estado === 'erro').length;
  const ativas = total - concluidas - erros;
  return { total, concluidas, erros, ativas, terminou: total > 0 && ativas === 0 };
}
